<template>
  <div class="form-container">
    <div class="form-header">
      <h2>Edit Video</h2>
    </div>
    <form @submit.prevent="handleSubmit" class="form">
      <VideoForm :channels="sortedChannels" ref="videoForm" />
      <div class="form-actions">
        <button type="submit" class="submit-button big">
          Save Changes
        </button>
        <button type="button" class="cancel-button medium" @click="handleCancel">
          Cancel
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { useTemplateRef, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import VideoForm from '@/components/VideoForm.vue';
import { isSuperuser } from '@/scripts/permissions';
import { handleApiError } from '@/scripts/shared-utils';
import { getVideo, updateVideo, videoKeys } from '@/scripts/api/videoApi';
import { getAllChannels, channelKeys } from '@/scripts/api/channelApi';

const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();

const videoId = computed(() => route.params.video_id);
const videoForm = useTemplateRef('videoForm');

if (!videoId.value) {
  console.error('Missing video ID');
  router.push('/');
}

// Fetch video data
const { data: videoData } = useQuery({
  queryKey: computed(() => videoKeys.getVideo(videoId.value)),
  queryFn: () => getVideo(videoId.value),
  enabled: computed(() => !!videoId.value),
  staleTime: 60000,
  onError: (err) => handleApiError(err, 'fetching video'),
});

// Fetch channels, only if superuser
const { data: channels } = useQuery({
  queryKey: channelKeys.getAllChannels(),
  queryFn: getAllChannels,
  enabled: isSuperuser(),
  staleTime: 60000,
  placeholderData: [],
  onError: (err) => handleApiError(err, 'fetching channels'),
});

const sortedChannels = computed(() => {
  return [...channels.value].sort((a, b) => a.name.localeCompare(b.name));
});

const formatDateForInput = (isoDate) => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

// Function to populate form with video data
const populateForm = () => {
  if (!videoData.value || !videoForm.value) return;
  
  const data = videoData.value;
  const form = videoForm.value.formData;
  
  for (const key in form) {
    if (data[key] !== undefined) {
      if (key.includes('date')) {
        form[key] = formatDateForInput(data[key]);
      } else {
        form[key] = data[key];
      }
    }
  }
};

// Watch for video data changes
watch(videoData, populateForm, { immediate: true });

// Also watch for videoForm to be available (in case it loads after videoData)
watch(videoForm, populateForm, { immediate: true });

// Mutation for updating the video
const updateVideoMutation = useMutation({
  mutationFn: (payload) => updateVideo(videoId.value, payload),
  onSuccess: () => {
    // Invalidate the specific video query
    queryClient.invalidateQueries({ queryKey: videoKeys.getVideo(videoId.value) });
    
    // Invalidate any video list queries
    queryClient.invalidateQueries({ queryKey: videoKeys.getAllVideos() });
    
    // Invalidate channel video queries if they exist
    if (videoData.value?.channel_id) {
      queryClient.invalidateQueries({ 
        queryKey: channelKeys.getChannelVideos(videoData.value.channel_id),
        refetchType: 'all'
      });
    }
    
    closeForm();
  },
  onError: (err) => {
    handleApiError(err, 'saving video');
    alert('Error saving video. Please try again.');
  },
});

const handleSubmit = () => {
  if (!videoForm.value) return;
  // The child form component holds all the fields, so we can submit its data directly
  updateVideoMutation.mutate(videoForm.value.formData);
};

const handleCancel = () => {
  router.back();
};

const closeForm = () => {
  router.push(isSuperuser() ? '/admin/publishing' : `/videos/${videoId.value}`);
};
</script>

<style scoped>
@import '@/styles/shared-form.css';
</style>
